import { sectionHref, type SettingsGroupId } from '@/lib/settings/registry';

/**
 * What an owner can DO about an error, carried beside the sentence that
 * explains it.
 *
 * An API error used to be one string. When the fix lived somewhere else in the
 * app, the string gave directions — "Add it in Settings → Workspaces → Business"
 * — and the client showed it in a toast that was gone in four seconds, with no
 * way to get there except remembering the route and walking it. The directions
 * were also free text, so nothing stopped them going stale: that exact sentence
 * named a Settings page that no longer existed.
 *
 * So a route that knows the fix returns it as data: `{ error, action }`. The
 * client (`useReportFailure`) turns an `action` into a notice with a button,
 * and a link built here from the settings registry cannot point somewhere the
 * registry does not know about.
 *
 * Directive-free on purpose: route handlers and client components both import
 * it (anti-pattern #18).
 */
export type ErrorAction =
  /** A button that takes them to the place the fix lives. */
  | { kind: 'link'; label: string; href: string }
  /** Nothing they can do themselves — offer "Tell the team". */
  | { kind: 'support' };

/**
 * A link to a Settings section, optionally scrolled to one setting on it.
 * `anchor` is the setting's `anchor` in `src/lib/settings/registry.ts`
 * (e.g. `set-vat`), which is the id the section renders.
 */
export function settingsAction(
  groupId: SettingsGroupId,
  sectionId: string,
  label: string,
  anchor?: string,
): ErrorAction {
  const base = sectionHref(groupId, sectionId);
  return { kind: 'link', label, href: anchor ? `${base}#${anchor}` : base };
}

/** Narrow an unknown JSON value to an `ErrorAction`, or null. The client's side of the contract. */
export function readErrorAction(value: unknown): ErrorAction | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (v.kind === 'support') return { kind: 'support' };
  if (
    v.kind === 'link'
    && typeof v.label === 'string' && v.label.trim()
    && typeof v.href === 'string'
    // Same-origin paths only. An error body is data from our own server, but
    // a button that can navigate anywhere is not a thing to leave open.
    && v.href.startsWith('/') && !v.href.startsWith('//')
  ) {
    return { kind: 'link', label: v.label, href: v.href };
  }
  return null;
}
